/**
 * collabConfig (web) — gates free-mode P2P collaboration. Collab is OFF unless
 * BOTH are true:
 *   - VITE_P2P_RELAY_MULTIADDR is set (the deployed circuit-relay address), and
 *   - the current URL carries a room link fragment (`#room=…&k=…`).
 *
 * Returns null otherwise, so today's behaviour is unchanged (local-first +
 * cross-tab BroadcastChannel + optional cloud bridge) until a relay is deployed
 * AND a collaborator opens a share link.
 *
 * The check here is deliberately lightweight (a hash presence test, no key
 * decode) so it stays out of the libp2p code path: the actual link is parsed
 * with `parseRoomLink` only inside the lazy-imported provider effect, keeping
 * the ~MBs of libp2p out of the main bundle until collaboration is active.
 */
export interface CollabConfig {
  /** Deployed circuit-relay multiaddr the browser node bootstraps from. */
  readonly relayMultiaddr: string;
}

/**
 * Pure gating decision (deterministic, unit-tested). Collab is ON only when a
 * relay is configured AND the URL fragment carries a `room` + `k`.
 */
export function resolveCollabConfig(
  relayMultiaddr: string | undefined,
  locationHash: string,
): CollabConfig | null {
  if (!relayMultiaddr) return null;
  const hash = locationHash.startsWith("#")
    ? locationHash.slice(1)
    : locationHash;
  const params = new URLSearchParams(hash);
  if (!params.get("room") || !params.get("k")) return null;
  return { relayMultiaddr };
}

function relayMultiaddr(): string | undefined {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  return env?.["VITE_P2P_RELAY_MULTIADDR"];
}

export function getCollabConfig(): CollabConfig | null {
  if (typeof location === "undefined") return null;
  return resolveCollabConfig(relayMultiaddr(), location.hash);
}

/**
 * Whether a relay is configured at all — i.e. whether offering "Share to
 * collaborate" is meaningful. Independent of the current URL (no room link
 * needed yet). Lets the UI hide the share affordance until a relay is deployed.
 */
export function isCollabRelayConfigured(): boolean {
  return !!relayMultiaddr();
}
